// Resolve/unresolve actions for the selected thread item (docs/06-
// ux-rules.md "Actions"; 11-chassis-plan.md §2 PR 05). Extracted out of
// `ThreadHeader`/`ThreadPane` — both need the same pending/error state, and
// the repo convention is to extract once 3+ hooks accumulate in a scene
// component (this hook alone already wraps two mutation hooks plus an
// effect).
import {
  useResolveFeedbackInboxItemMutation,
  useUnresolveFeedbackInboxItemMutation,
} from 'api/feedbackInbox'
import type {
  FeedbackInboxDetail,
  ResolveFeedbackInboxItemBody,
} from 'api/feedbackInbox.types'
import { isFetchBaseQueryError } from 'api/utils'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useRef, useState } from 'react'

const t = buildTranslate('feedback_inbox')

export interface UseInboxActionsOptions {
  detail: FeedbackInboxDetail
  onResolved: (id: string) => void
  // Failure-only signal (mirrors `onResolved`) — releases a held
  // auto-advance (`useResolveAutoAdvance`'s `rollbackResolved`) once the
  // mutation itself rejects, rather than leaving it to guess from the
  // optimistic patch's own rollback alone.
  onResolveFailed?: (id: string) => void
  announce: (text: string) => void
}

export type InboxErrorKind = 'network' | 'validation' | 'permission'
// The control that fired the write: decides which surface shows its failure.
export type InboxAction = 'send' | 'mark_resolved' | 'unresolve'
export type ResolveAction = Exclude<InboxAction, 'unresolve'>
export interface InboxActionFailure {
  action: InboxAction
  kind: InboxErrorKind
}

export interface UseInboxActionsResult {
  // `action` names the control; omitted = the composer's send.
  resolve: (
    body?: ResolveFeedbackInboxItemBody,
    action?: ResolveAction
  ) => Promise<void>
  unresolve: () => Promise<void>
  isPending: boolean
  // True iff the most recently fired write failed.
  error: boolean
  // Always set by this hook. Optional only so hand-built `actions` literals
  // (AI-1358-owned tests/stories) that set just `error` keep type-checking;
  // `actionFailure` reads those as a send network failure (pre-AI-1350
  // copy).
  failure?: InboxActionFailure | null
  reset: () => void
}

const HTTP_FORBIDDEN = 403
// 400 = nexus's reward_ids cap guard; 422 = already resolved / ineligible
// reward.
const VALIDATION_STATUSES = new Set<number>([400, 422])

// RTK rejection → banner mode (S4 §3.2 F). Network covers 5xx, FETCH_ERROR,
// TIMEOUT_ERROR, PARSING_ERROR, CUSTOM_ERROR, a SerializedError, and any
// other status (e.g. 404): "try again" is the honest advice for all of them.
// Status-only — never reads the body, so it's independent of the three 403
// body shapes and never renders untranslated server text.
export function classifyInboxError(error: unknown): InboxErrorKind {
  if (!isFetchBaseQueryError(error)) return 'network'
  const { status } = error
  if (typeof status !== 'number') return 'network'
  if (status === HTTP_FORBIDDEN) return 'permission'
  if (VALIDATION_STATUSES.has(status)) return 'validation'
  return 'network'
}

// Normalizes `failure` for both the real hook (always sets it) and hand-
// built `actions` literals (AI-1358-owned tests/stories) that set only
// `error` — those read as a send/network failure, matching the pre-AI-1350
// copy those literals were written to exercise.
export function actionFailure(
  actions: Pick<UseInboxActionsResult, 'error' | 'failure'>
): InboxActionFailure | null {
  if (actions.failure !== undefined) return actions.failure
  return actions.error ? { action: 'send', kind: 'network' } : null
}

export function useInboxActions({
  detail,
  onResolved,
  onResolveFailed,
  announce,
}: UseInboxActionsOptions): UseInboxActionsResult {
  const [triggerResolve, resolveState] = useResolveFeedbackInboxItemMutation()
  const [triggerUnresolve, unresolveState] =
    useUnresolveFeedbackInboxItemMutation()

  // "Last control wins": which write most recently fired, so a stale error
  // from an earlier action can't outlive a later successful one without
  // calling `reset()` from inside a trigger (RTK `reset` identity churns —
  // see memory rtk-reset-identity-churn-kills-retry-state).
  const [lastAction, setLastAction] = useState<InboxAction | null>(null)
  // Synchronous double-submit guard (S4 F9): `isPending` reaches the buttons
  // a render late. Keyed by item so an item switch mid-flight never blocks
  // the new item's controls, and an old request settling never clears a
  // newer item's guard.
  const inFlightIdRef = useRef<string | null>(null)

  // Mutation error/result state is instance-scoped and survives navigation
  // (CLAUDE.md mutation rule; docs/06-ux-rules.md "Actions" / "Error
  // reset") — reset both whenever the selected item changes so a stale
  // error banner from a PREVIOUS item's failed resolve doesn't carry over.
  // `lastAction` itself can stay: once both RTK states are reset, `isError`
  // is false on whichever one it points at, so `failure` (below) goes null.
  useEffect(() => {
    resolveState.reset()
    unresolveState.reset()
    // Only a real item change should trigger this — `resolveState`/
    // `unresolveState` are fresh objects every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.id])

  const resolve = useCallback(
    async (
      body: ResolveFeedbackInboxItemBody = {
        resolution_type: 'resolved_without_reward',
      },
      action: ResolveAction = 'send'
    ) => {
      const id = detail.id
      if (inFlightIdRef.current === id) return
      inFlightIdRef.current = id
      setLastAction(action)
      try {
        await triggerResolve({ id, body }).unwrap()
      } catch {
        // Surfaced via `failure` below — the inline banner is ThreadPane's
        // (or the composer's) concern, not this hook's.
        onResolveFailed?.(id)
        return
      } finally {
        if (inFlightIdRef.current === id) inFlightIdRef.current = null
      }
      // Outside the try — an error here must not be misattributed to a
      // resolve failure (the mutation already succeeded server-side).
      onResolved(id)
    },
    [triggerResolve, detail.id, onResolved, onResolveFailed]
  )

  const unresolve = useCallback(async () => {
    const id = detail.id
    if (inFlightIdRef.current === id) return
    inFlightIdRef.current = id
    setLastAction('unresolve')
    try {
      await triggerUnresolve(id).unwrap()
      announce(t('actions.unresolved_announcement'))
    } catch {
      // Surfaced via `failure` below.
    } finally {
      if (inFlightIdRef.current === id) inFlightIdRef.current = null
    }
  }, [triggerUnresolve, detail.id, announce])

  const reset = useCallback(() => {
    resolveState.reset()
    unresolveState.reset()
  }, [resolveState, unresolveState])

  const lastState = lastAction === 'unresolve' ? unresolveState : resolveState
  const failure: InboxActionFailure | null =
    lastAction !== null && lastState.isError
      ? { action: lastAction, kind: classifyInboxError(lastState.error) }
      : null

  return {
    resolve,
    unresolve,
    isPending: resolveState.isLoading || unresolveState.isLoading,
    error: failure !== null,
    failure,
    reset,
  }
}
